import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import Webcam from "react-webcam";
import api, { clearSession, getProfile } from "../api";
import LocationMapPicker from "../components/LocationMapPicker";
import PayPalSubscribeButton from "../components/PayPalSubscribeButton";

const s = {
  page: { display: "flex", minHeight: "100vh" },
  sidebar: { width: 220, background: "#111827", color: "#fff", padding: 24 },
  navItem: (active) => ({ padding: "10px 12px", borderRadius: 6, marginBottom: 6, cursor: "pointer", background: active ? "#2563eb" : "transparent" }),
  main: { flex: 1, padding: 32, overflowY: "auto" },
  card: { background: "#fff", borderRadius: 10, padding: 20, marginBottom: 20, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" },
  input: { padding: 8, marginRight: 8, marginBottom: 8, border: "1px solid #ccc", borderRadius: 6 },
  button: { padding: "8px 14px", background: "#2563eb", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { textAlign: "left", padding: 8, borderBottom: "2px solid #eee", fontSize: 13, color: "#666" },
  td: { padding: 8, borderBottom: "1px solid #f0f0f0", fontSize: 14 },
  badge: (color) => ({ padding: "2px 8px", borderRadius: 12, fontSize: 12, background: color + "22", color }),
};

export default function AdminDashboard() {
  const [tab, setTab] = useState("labours");
  const navigate = useNavigate();
  const profile = getProfile();

  const logout = () => { clearSession(); navigate("/login"); };

  return (
    <div style={s.page} className="app-page">
      <div style={s.sidebar} className="app-sidebar">
        <h3 style={{ marginTop: 0 }}>{profile?.name || "Admin"}</h3>
        {["analytics", "labours", "locations", "approvals", "payments", "kiosk", "billing"].map((t) => (
          <div key={t} style={s.navItem(tab === t)} className="app-nav-item" onClick={() => setTab(t)}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </div>
        ))}
        <div style={{ ...s.navItem(false), marginTop: 30, color: "#f87171" }} onClick={logout}>Log out</div>
      </div>
      <div style={s.main} className="app-main">
        {tab === "analytics" && <AnalyticsTab />}
        {tab === "labours" && <LaboursTab />}
        {tab === "locations" && <LocationsTab />}
        {tab === "approvals" && <ApprovalsTab />}
        {tab === "payments" && <PaymentsTab />}
        {tab === "kiosk" && <KioskTab />}
        {tab === "billing" && <BillingTab />}
      </div>
    </div>
  );
}

// ------------------------------------------------------------ Analytics ---

function AnalyticsTab() {
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState([]);
  const [byLocation, setByLocation] = useState([]);

  useEffect(() => {
    api.get("/api/admin/analytics/summary").then((r) => setSummary(r.data));
    api.get("/api/admin/analytics/hours-trend?days=30").then((r) => setTrend(r.data));
    api.get("/api/admin/analytics/by-location").then((r) => setByLocation(r.data));
  }, []);

  const statCard = (label, value) => (
    <div style={{ ...s.card, flex: 1, textAlign: "center" }}>
      <div style={{ fontSize: 13, color: "#666" }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700 }}>{value}</div>
    </div>
  );

  return (
    <div>
      <h2>Analytics</h2>
      {summary && (
        <div style={{ display: "flex", gap: 16, marginBottom: 4 }}>
          {statCard("Active labours", `${summary.active_labours}/${summary.total_labours}`)}
          {statCard("Locations", summary.total_locations)}
          {statCard("Hours this month", summary.hours_this_month)}
          {statCard("Payroll this month", summary.payroll_this_month)}
          {statCard("Pending approvals", summary.pending_approvals)}
        </div>
      )}

      <div style={s.card}>
        <h4>Hours worked — last 30 days</h4>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={trend}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} />
            <YAxis />
            <Tooltip />
            <Line type="monotone" dataKey="hours" stroke="#2563eb" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div style={s.card}>
        <h4>Cost by location (this month)</h4>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={byLocation}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="location_name" tick={{ fontSize: 11 }} />
            <YAxis />
            <Tooltip />
            <Bar dataKey="total_cost" fill="#2563eb" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ------------------------------------------------------------- Labours ---

function LaboursTab() {
  const [labours, setLabours] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [faceFile, setFaceFile] = useState(null);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState(null);           // "Manage faces" row
  const [attendanceLabourId, setAttendanceLabourId] = useState(null); // attendance viewer row
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/api/admin/labours").then((r) => setLabours(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;           // blocks a second click while the first is still in flight
    setError("");
    setBusy(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (faceFile) fd.append("face_image", faceFile);
    try {
      await api.post("/api/admin/labours", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ name: "", email: "", phone: "", password: "" });
      setFaceFile(null);
      load();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to add labour");
    }finally {
      setBusy(false);        
    }
  };

  return (
    <div>
      <h2>Labours</h2>
      <div style={s.card}>
        <h4>Add a new labour</h4>
        {error && <div style={{ color: "#dc2626", marginBottom: 8 }}>{error}</div>}
        <form onSubmit={submit}>
          <input style={s.input} placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          <input style={s.input} placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
          <input style={s.input} placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <input style={s.input} placeholder="Password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
          <br />
          <label style={{ fontSize: 13, color: "#555" }}>
            Face photo (optional now, can add more later for better matching):{" "}
            <input type="file" accept="image/*" onChange={(e) => setFaceFile(e.target.files[0])} />
          </label>
          <br /><br />
          <button style={{ ...s.button, opacity: busy ? 0.6 : 1 }} type="submit" disabled={busy}>
  {busy ? "Adding..." : "Add labour"}
</button>
        </form>
      </div>

      <div style={s.card}>
        <table style={s.table}>
          <thead>
            <tr><th style={s.th}>Name</th><th style={s.th}>Email</th><th style={s.th}>Phone</th><th style={s.th}>Face photos</th><th style={s.th}>Status</th><th style={s.th}></th></tr>
          </thead>
          <tbody>
            {labours.map((l) => (
              <React.Fragment key={l.id}>
                <tr>
                  <td style={s.td}>
                    <span
                      style={{ color: "#2563eb", cursor: "pointer", textDecoration: "underline" }}
                      onClick={() => setAttendanceLabourId(attendanceLabourId === l.id ? null : l.id)}
                    >
                      {l.name}
                    </span>
                  </td>
                  <td style={s.td}>{l.email}</td>
                  <td style={s.td}>{l.phone || "—"}</td>
                  <td style={s.td}>
                    {l.face_photo_count > 0
                      ? <span style={s.badge("#16a34a")}>{l.face_photo_count} photo(s)</span>
                      : <span style={s.badge("#dc2626")}>None</span>}
                  </td>
                  <td style={s.td}>{l.is_active ? "Active" : "Deactivated"}</td>
                  <td style={s.td}>
                    <button style={{ ...s.button, background: "#6b7280", padding: "4px 10px" }}
                      onClick={() => setExpandedId(expandedId === l.id ? null : l.id)}>
                      {expandedId === l.id ? "Close" : "Manage faces"}
                    </button>
                  </td>
                </tr>
                {expandedId === l.id && (
                  <tr>
                    <td style={s.td} colSpan={6}>
                      <FaceManager labourId={l.id} onChange={load} />
                    </td>
                  </tr>
                )}
                {attendanceLabourId === l.id && (
                  <tr>
                    <td style={s.td} colSpan={6}>
                      <LabourAttendanceViewer labourId={l.id} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FaceManager({ labourId, onChange }) {
  const [photos, setPhotos] = useState([]);
  const [file, setFile] = useState(null);
  const [label, setLabel] = useState("");
  const [error, setError] = useState("");

  const load = () => api.get(`/api/admin/labours/${labourId}/face`).then((r) => setPhotos(r.data));
  useEffect(() => { load(); }, [labourId]); // eslint-disable-line

  const addPhoto = async () => {
    if (!file) return;
    setError("");
    const fd = new FormData();
    fd.append("face_image", file);
    if (label) fd.append("label", label);
    try {
      await api.post(`/api/admin/labours/${labourId}/face`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFile(null);
      setLabel("");
      load();
      onChange && onChange();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to add photo");
    }
  };

  const removePhoto = async (id) => {
    await api.delete(`/api/admin/labours/${labourId}/face/${id}`);
    load();
    onChange && onChange();
  };

  return (
    <div style={{ background: "#f9fafb", padding: 14, borderRadius: 8 }}>
      <strong>Registered photos (different angles/lighting improve matching):</strong>
      {error && <div style={{ color: "#dc2626", margin: "6px 0" }}>{error}</div>}
      <ul style={{ paddingLeft: 18 }}>
        {photos.map((p) => (
          <li key={p.id} style={{ marginBottom: 4 }}>
            {p.label || "unlabeled"} — added {new Date(p.created_at).toLocaleDateString()}{" "}
            <button style={{ ...s.button, background: "#dc2626", padding: "2px 8px", fontSize: 12 }} onClick={() => removePhoto(p.id)}>Remove</button>
          </li>
        ))}
        {photos.length === 0 && <li>No photos yet.</li>}
      </ul>
      <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0])} />
      <input style={{ ...s.input, marginLeft: 8 }} placeholder="Label (optional, e.g. 'with glasses')" value={label} onChange={(e) => setLabel(e.target.value)} />
      <button style={s.button} onClick={addPhoto}>Add photo</button>
    </div>
  );
}

function LabourAttendanceViewer({ labourId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/api/admin/labours/${labourId}/attendance`).then((r) => {
      setData(r.data);
      setLoading(false);
    });
  }, [labourId]);

  if (loading) return <div style={{ padding: 14 }}>Loading attendance...</div>;
  if (!data) return null;

  const statusColor = { approved: "#16a34a", pending: "#d97706", rejected: "#dc2626" };

  return (
    <div style={{ background: "#f9fafb", padding: 14, borderRadius: 8 }}>
      <strong>{data.labour.name}'s attendance — total approved hours: {data.total_approved_hours}</strong>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Date</th><th style={s.th}>Location</th><th style={s.th}>In</th>
            <th style={s.th}>Out</th><th style={s.th}>Hours</th><th style={s.th}>Source</th><th style={s.th}>Status</th>
          </tr>
        </thead>
        <tbody>
          {data.records.map((r) => (
            <tr key={r.id}>
              <td style={s.td}>{r.attendance_date}</td>
              <td style={s.td}>{r.location_name}</td>
              <td style={s.td}>{new Date(r.intime).toLocaleTimeString()}</td>
              <td style={s.td}>{r.outtime ? new Date(r.outtime).toLocaleTimeString() : "—"}</td>
              <td style={s.td}>{r.hours_worked}</td>
              <td style={s.td}>{r.source}</td>
              <td style={s.td}><span style={s.badge(statusColor[r.status])}>{r.status}</span></td>
            </tr>
          ))}
          {data.records.length === 0 && (
            <tr><td style={s.td} colSpan={7}>No attendance records yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ------------------------------------------------------------ Locations ---

function LocationsTab() {
  const [locations, setLocations] = useState([]);
  const [labours, setLabours] = useState([]);
  const [form, setForm] = useState({
    name: "", address: "", latitude: "", longitude: "", geofence_radius_m: 100,
    hourly_rate: "", ot_hourly_rate: "", standard_hours_per_day: 8,
  });
  const [assign, setAssign] = useState({ location_id: "", labour_id: "" });
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/api/admin/locations").then((r) => setLocations(r.data));
    api.get("/api/admin/labours").then((r) => setLabours(r.data));
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError("");
    try {
      await api.post("/api/admin/locations", form);
      setForm({ name: "", address: "", latitude: "", longitude: "", geofence_radius_m: 100, hourly_rate: "", ot_hourly_rate: "", standard_hours_per_day: 8 });
      load();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to add location");
    }finally {
      setBusy(false);          
    }
  };

  const submitAssign = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/api/admin/locations/${assign.location_id}/assign`, { labour_id: Number(assign.labour_id) });
      setAssign({ location_id: "", labour_id: "" });
      alert("Assigned");
    } catch (err) {
      alert(err.response?.data?.error || "Failed to assign");
    }
  };

  return (
    <div>
      <h2>Locations</h2>
      <div style={s.card}>
        <h4>Add a location</h4>
        {error && <div style={{ color: "#dc2626", marginBottom: 8 }}>{error}</div>}
        <form onSubmit={submit}>
          <input style={s.input} placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          <br />
          <LocationMapPicker
            onChange={({ lat, lng, address }) =>
              setForm((f) => ({ ...f, latitude: lat, longitude: lng, address: address || f.address }))
            }
          />
          <p style={{ fontSize: 12, color: "#666", marginTop: 6 }}>
             Selected: {form.address || (form.latitude && form.longitude ? `${Number(form.latitude).toFixed(6)}, ${Number(form.longitude).toFixed(6)}` : "none yet")}
          </p>
          <input style={s.input} placeholder="Geofence radius (m)" type="number" value={form.geofence_radius_m} onChange={(e) => setForm({ ...form, geofence_radius_m: e.target.value })} />
          <br />
          <input style={s.input} placeholder="Regular hourly rate" type="number" step="0.01" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} required />
          <input style={s.input} placeholder="OT hourly rate (optional)" type="number" step="0.01" value={form.ot_hourly_rate} onChange={(e) => setForm({ ...form, ot_hourly_rate: e.target.value })} />
          <input style={s.input} placeholder="Standard hrs/day before OT" type="number" step="0.5" value={form.standard_hours_per_day} onChange={(e) => setForm({ ...form, standard_hours_per_day: e.target.value })} />
          <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
            Hours worked beyond "standard hrs/day" on any given day are paid at the OT rate. Leave OT rate blank to use the regular rate for overtime too.
          </div>
          <button style={{ ...s.button, opacity: busy ? 0.6 : 1 }} type="submit" disabled={busy}>
  {busy ? "Saving..." : "Add location"}
</button>
        </form>
      </div>

      <div style={s.card}>
        <h4>Assign a labour to a location</h4>
        <form onSubmit={submitAssign}>
          <select style={s.input} value={assign.location_id} onChange={(e) => setAssign({ ...assign, location_id: e.target.value })} required>
            <option value="">Select location</option>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
          <select style={s.input} value={assign.labour_id} onChange={(e) => setAssign({ ...assign, labour_id: e.target.value })} required>
            <option value="">Select labour</option>
            {labours.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
          <button style={s.button} type="submit">Assign</button>
        </form>
      </div>

      <div style={s.card}>
        <table style={s.table}>
          <thead>
            <tr><th style={s.th}>Name</th><th style={s.th}>address</th><th style={s.th}>Radius (m)</th><th style={s.th}>Rate/hr</th><th style={s.th}>OT rate/hr</th><th style={s.th}>Std hrs/day</th><th style={s.th}></th></tr>
          </thead>
          <tbody>
            {locations.map((l) => (
              <React.Fragment key={l.id}>
                <tr>
                  <td style={s.td}>{l.name}</td>
                  <td style={s.td}>{l.address || `${l.latitude}, ${l.longitude}`}</td>
                  <td style={s.td}>{l.geofence_radius_m}</td>
                  <td style={s.td}>{l.current_hourly_rate}</td>
                  <td style={s.td}>{l.current_ot_hourly_rate ?? "(= regular)"}</td>
                  <td style={s.td}>{l.standard_hours_per_day}</td>
                  <td style={s.td}>
                    <button style={{ ...s.button, background: "#6b7280", padding: "4px 10px" }}
                      onClick={() => setExpandedId(expandedId === l.id ? null : l.id)}>
                      {expandedId === l.id ? "Close" : "Rate & OT dates"}
                    </button>
                  </td>
                </tr>
                {expandedId === l.id && (
                  <tr><td style={s.td} colSpan={7}><LocationRateManager location={l} onChange={load} /></td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LocationRateManager({ location, onChange }) {
  const [rateForm, setRateForm] = useState({
    hourly_rate: location.current_hourly_rate || "",
    ot_hourly_rate: location.current_ot_hourly_rate || "",
    standard_hours_per_day: location.standard_hours_per_day || 8,
  });
  const [otDates, setOtDates] = useState([]);
  const [newOtDate, setNewOtDate] = useState({ ot_date: "", reason: "" });
  const [message, setMessage] = useState("");

  const loadOtDates = () => api.get(`/api/admin/locations/${location.id}/ot-dates`).then((r) => setOtDates(r.data));
  useEffect(() => { loadOtDates(); }, [location.id]); // eslint-disable-line

  const updateRate = async () => {
    setMessage("");
    try {
      await api.put(`/api/admin/locations/${location.id}/rate`, rateForm);
      setMessage("Rate updated — takes effect from today onward, past payroll is unaffected.");
      onChange && onChange();
    } catch (err) {
      setMessage(err.response?.data?.error || "Failed to update rate");
    }
  };

  const addOtDate = async () => {
    if (!newOtDate.ot_date) return;
    try {
      await api.post(`/api/admin/locations/${location.id}/ot-dates`, newOtDate);
      setNewOtDate({ ot_date: "", reason: "" });
      loadOtDates();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to add OT date");
    }
  };

  const removeOtDate = async (id) => {
    await api.delete(`/api/admin/locations/${location.id}/ot-dates/${id}`);
    loadOtDates();
  };

  return (
    <div style={{ background: "#f9fafb", padding: 14, borderRadius: 8 }}>
      <strong>Update rate (starts a new rate period from today; past months are unaffected)</strong>
      <br /><br />
      <input style={s.input} type="number" step="0.01" placeholder="Hourly rate" value={rateForm.hourly_rate} onChange={(e) => setRateForm({ ...rateForm, hourly_rate: e.target.value })} />
      <input style={s.input} type="number" step="0.01" placeholder="OT hourly rate" value={rateForm.ot_hourly_rate} onChange={(e) => setRateForm({ ...rateForm, ot_hourly_rate: e.target.value })} />
      <input style={s.input} type="number" step="0.5" placeholder="Standard hrs/day" value={rateForm.standard_hours_per_day} onChange={(e) => setRateForm({ ...rateForm, standard_hours_per_day: e.target.value })} />
      <button style={s.button} onClick={updateRate}>Update rate</button>
      {message && <p style={{ fontSize: 13 }}>{message}</p>}

      <hr style={{ margin: "16px 0" }} />

      <strong>Forced OT dates (holidays — entire day pays OT rate regardless of hours)</strong>
      <ul style={{ paddingLeft: 18 }}>
        {otDates.map((d) => (
          <li key={d.id}>
            {d.ot_date} {d.reason ? `— ${d.reason}` : ""}{" "}
            <button style={{ ...s.button, background: "#dc2626", padding: "2px 8px", fontSize: 12 }} onClick={() => removeOtDate(d.id)}>Remove</button>
          </li>
        ))}
        {otDates.length === 0 && <li>None set.</li>}
      </ul>
      <input style={s.input} type="date" value={newOtDate.ot_date} onChange={(e) => setNewOtDate({ ...newOtDate, ot_date: e.target.value })} />
      <input style={s.input} placeholder="Reason (optional)" value={newOtDate.reason} onChange={(e) => setNewOtDate({ ...newOtDate, reason: e.target.value })} />
      <button style={s.button} onClick={addOtDate}>Add OT date</button>
    </div>
  );
}

// ------------------------------------------------------------ Approvals ---


function ApprovalsTab() {
  const [pending, setPending] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const res = await api.get("/api/admin/attendance/pending");
      setPending(res.data);
    } catch (err) {
      setError("Failed to load pending attendance requests.");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const act = async (id, action) => {
    setBusyId(id);
    setError("");

    try {
      // Send approval or rejection request
      await api.post(`/api/admin/attendance/${id}/${action}`);

      // Immediately remove processed request from UI
      setPending((prev) => prev.filter((p) => p.id !== id));

    } catch (err) {
      setError(
        err.response?.data?.error || "Failed to process attendance request."
      );
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <h2>Manual Attendance Approvals</h2>

      {error && (
        <p style={{ color: "red" }}>{error}</p>
      )}

      <div style={s.card}>
        {pending.length === 0 && (
          <p>No pending requests.</p>
        )}

        {pending.length > 0 && (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Labour</th>
                <th style={s.th}>Location</th>
                <th style={s.th}>Date</th>
                <th style={s.th}>In</th>
                <th style={s.th}>Out</th>
                <th style={s.th}>Hours</th>
                <th style={s.th}>Notes</th>
                <th style={s.th}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {pending.map((p) => (
                <tr key={p.id}>
                  <td style={s.td}>{p.labour_name}</td>
                  <td style={s.td}>{p.location_name}</td>
                  <td style={s.td}>{p.attendance_date}</td>

                  <td style={s.td}>
                    {new Date(p.intime).toLocaleTimeString()}
                  </td>

                  <td style={s.td}>
                    {p.outtime
                      ? new Date(p.outtime).toLocaleTimeString()
                      : "—"}
                  </td>

                  <td style={s.td}>{p.hours_worked}</td>
                  <td style={s.td}>{p.notes || "—"}</td>

                  <td style={s.td}>
                    <button
                      disabled={busyId !== null}
                      style={{
                        ...s.button,
                        background: "#16a34a",
                        marginRight: 6,
                        opacity: busyId !== null ? 0.5 : 1
                      }}
                      onClick={() => act(p.id, "approve")}
                    >
                      {busyId === p.id ? "Processing..." : "Approve"}
                    </button>

                    <button
                      disabled={busyId !== null}
                      style={{
                        ...s.button,
                        background: "#dc2626",
                        opacity: busyId !== null ? 0.5 : 1
                      }}
                      onClick={() => act(p.id, "reject")}
                    >
                      {busyId === p.id ? "Processing..." : "Reject"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------- Payments ---

function PaymentsTab() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState({ payments: [], totals_by_location: {} });

  const load = () => api.get(`/api/admin/payments?year=${year}&month=${month}`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [year, month]); // eslint-disable-line

  const generate = async () => {
    await api.post("/api/admin/payments/generate", { year: Number(year), month: Number(month) });
    load();
  };

  return (
    <div>
      <h2>Payments</h2>
      <div style={s.card}>
        <input style={s.input} type="number" value={year} onChange={(e) => setYear(e.target.value)} />
        <input style={s.input} type="number" min="1" max="12" value={month} onChange={(e) => setMonth(e.target.value)} />
        <button style={s.button} onClick={generate}>Generate / recalculate for this month</button>
      </div>

      <div style={s.card}>
        <h4>Totals by location</h4>
        {Object.entries(data.totals_by_location).map(([id, v]) => (
          <div key={id}>{v.location_name}: <strong>{v.total.toFixed(2)}</strong></div>
        ))}
        {Object.keys(data.totals_by_location).length === 0 && <p>No payments generated yet for this period.</p>}
      </div>

      <div style={s.card}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Location</th><th style={s.th}>Regular hrs</th><th style={s.th}>OT hrs</th>
              <th style={s.th}>Rate</th><th style={s.th}>OT rate</th><th style={s.th}>Total amount</th>
            </tr>
          </thead>
          <tbody>
            {data.payments.map((p) => (
              <tr key={p.id}>
                <td style={s.td}>{p.location_name}</td>
                <td style={s.td}>{p.regular_hours}</td>
                <td style={s.td}>{p.ot_hours}</td>
                <td style={s.td}>{p.hourly_rate}</td>
                <td style={s.td}>{p.ot_hourly_rate ?? "—"}</td>
                <td style={s.td}><strong>{p.total_amount}</strong></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// --------------------------------------------------------------- Kiosk ---

function KioskTab() {
  const webcamRef = React.useRef(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const capture = async () => {
    setBusy(true);
    setMessage("");
    try {
      const shot = webcamRef.current.getScreenshot();
      const position = await new Promise((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true })
      );
      const blob = await (await fetch(shot)).blob();
      const fd = new FormData();
      fd.append("face_image", blob, "face.jpg");
      fd.append("latitude", position.coords.latitude);
      fd.append("longitude", position.coords.longitude);

      const res = await api.post("/api/admin/kiosk/checkin", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMessage(`✓ ${res.data.action === "checkin" ? "Checked in" : "Checked out"}: ${res.data.labour_name} — ${new Date().toLocaleTimeString()}`);
    } catch (err) {
      setMessage(`✗ ${err.response?.data?.error || "Failed"}`);
    }
    setBusy(false);
  };

  return (
    <div>
      <h2>Kiosk Mode</h2>
      <div style={s.card}>
        <p style={{ fontSize: 13, color: "#666" }}>
          For a shared device at the site entrance. Any registered labour steps up, the face itself identifies who they are —
          no individual login needed. Automatically checks in if they have no open record today, or checks out if they do.
        </p>
        <Webcam audio={false} ref={webcamRef} screenshotFormat="image/jpeg" width={360} videoConstraints={{ facingMode: "user" }} />
        <br /><br />
        <button style={s.button} disabled={busy} onClick={capture}>Scan face</button>
        {message && <p style={{ marginTop: 16, fontWeight: 600 }}>{message}</p>}
      </div>
    </div>
  );
}

// -------------------------------------------------------------- Billing ---

function BillingTab() {
  const [status, setStatus] = useState(null);
  const [plansData, setPlansData] = useState(null);

  const load = () => {
    api.get("/api/billing/status").then((r) => setStatus(r.data));
    api.get("/api/billing/paypal/plans").then((r) => setPlansData(r.data));
  };
  useEffect(() => { load(); }, []);

  return (
    <div>
      <h2>Billing & Subscription</h2>
      <div style={s.card}>
        {status && (
          <>
            <p><strong>Status:</strong> {status.active ? <span style={s.badge("#16a34a")}>Active ({status.plan_name})</span> : <span style={s.badge("#dc2626")}>Inactive</span>}</p>
            {status.active && (
              <p><strong>Renews / expires:</strong> {status.end_date} ({status.days_left} day(s) left)</p>
            )}
          </>
        )}
      </div>

      {plansData && (
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          {plansData.plans.map((p) => (
            <div key={p.key} style={{ ...s.card, flex: 1, minWidth: 220, opacity: p.fits_current_labours ? 1 : 0.6 }}>
              <h3 style={{ marginTop: 0 }}>{p.label}</h3>
              <p style={{ color: "#666" }}>Up to {p.max_labours ?? "unlimited"} labours</p>
              <p style={{ fontSize: 24, fontWeight: 700, color: "#2563eb" }}>USD {p.price_lkr}<span style={{ fontSize: 13, color: "#666" }}>/mo</span></p>
              {!p.fits_current_labours && (
                <p style={{ color: "#dc2626", fontSize: 12 }}>
                  You have {plansData.current_labour_count} labours — too many for this plan
                </p>
              )}
              <PayPalSubscribeButton
                clientId={plansData.client_id}
                planId={p.paypal_plan_id}
                planKey={p.key}
                onSubscribed={() => load()}
              />
            </div>
          ))}
        </div>
      )}

    
    </div>
  );
}